import React, { createContext, useContext, useState, useEffect } from "react";
import { CartItem } from "../types/cart";

type CartContextType = {
  cart: CartItem[];
  addToCart: (p: Omit<CartItem, "quantity">) => void;
  updateQuantity: (id: string, size: string, qty: number) => void;
  removeFromCart: (id: string, size: string) => void;
  clearCart: () => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    const stored = localStorage.getItem("cart");
    const expiration = localStorage.getItem("cart_expiration");

    const now = Date.now();
    const expiresAt = expiration ? parseInt(expiration, 10) : 0;

    // Si le panier est expiré
    if (expiresAt && now > expiresAt) {
      localStorage.removeItem("cart");
      localStorage.removeItem("cart_expiration");
      return [];
    }

    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));

    // Définir expiration à +45 minutes
    const expiresAt = Date.now() + 1000 * 60 * 45;
    localStorage.setItem("cart_expiration", expiresAt.toString());
  }, [cart]);

  const addToCart = (product: Omit<CartItem, "quantity">) => {
    setCart((prev) => {
      const existing = prev.find(
        (item) => item._id === product._id && item.size === product.size
      );
      if (existing) {
        return prev.map((item) =>
          item._id === product._id && item.size === product.size
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id: string, size: string, qty: number) => {
    setCart((prev) =>
      prev.map((it) =>
        it._id === id && it.size === size ? { ...it, quantity: qty } : it
      )
    );
  };

  const removeFromCart = (productId: string, size: string) => {
    setCart((prev) =>
      prev.filter((item) => !(item._id === productId && item.size === size))
    );
  };

  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider
      value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
};
